import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { Row } from 'antd';
import { StripeProvider } from 'react-stripe-elements';

import config from '$config';
import Loading from '$app/shared/components/Loading';
import LoadingModal from '$app/shared/components/LoadingModal';
import injectSaga from '$app/utils/injectSaga';
import injectReducer from '$app/utils/injectReducer';
import { isNotificationMarker } from '$app/core/utils/noteNotification';

import { makeSelectBooking, getLoading, getAuthUser, getBookingChanged, getBookingReloading } from './selectors';
import { Container, ColWithPadding, MainDiv } from './styled';
import reducer from './reducer';
import saga from './saga';
import {
  chargePaymentAction,
  chargeInstalmentAction,
  addNoteAction,
  cancelBookingAction,
  patchBookingAction,
  updateGarageBillStatus,
  getGarageBillPdfUrlRequest,
  reloadBooking,
  updateBookingAction,
} from './actions';
import { BOOKING_STATUS } from './constants';
import GarageInvoiceDetails from './GarageInvoice';
import BookingHeader from './Header';
import WorkDetails from './WorkDetails';
import CaseManagerNotes from './CaseManagerNotes';
import BookingDetails from './BookingDetails';
import GarageAllocation from './GarageAllocation';
import HeroTrips from './HeroTrips';
import CustomerNotes from './CustomerNotes';
import Gallery from './Gallery';
import ReminderList from './Reminder';
import CancelledWorkDetails from './CancelledWorkDetails';
import RefreshBanner from './RefreshBanner';
import KlarnaReauthorisationAlert from './KlarnaReauthorisationAlert';
import { shouldShowKlarnaReauthorisationLink } from './KlarnaReauthorisationAlert/logic';
import BookingPageContext from './context';

class BookingPage extends PureComponent {
  state = {
    noteMarker: false,
    garageOperations: {},
  };

  updateBooking = (updater) => {
    const { booking } = this.props;
    if (!booking) return;

    this.props.updateBooking(updater(booking));
  };

  setTags = (tags) => {
    this.updateBooking((booking) => ({
      ...booking,
      tags,
    }));
  };

  addBookingNote = (note) => {
    this.updateBooking((booking) => ({
      ...booking,
      notes: [note, ...(booking.notes || [])],
    }));
  };

  updateTripAssignment = (tripAssignments) => {
    const assignmentsByTripId = tripAssignments.reduce((assignments, assignment) => {
      assignments[assignment.tripId] = assignment;
      return assignments;
    }, {});

    this.updateBooking((booking) => ({
      ...booking,
      trips: (booking.trips || []).map((trip) => {
        const assignment = assignmentsByTripId[trip.tripId];
        if (!assignment) return trip;

        return {
          ...trip,
          pickerId: assignment.heroId,
          heroId: assignment.heroId,
          driverRate: assignment.driverRate,
          allocationStatus: assignment.allocationStatus || 'pending',
        };
      }),
    }));
  };

  handleGarageOperationsLoaded = (garageOperations) => {
    this.setState((state) => ({
      garageOperations: {
        ...state.garageOperations,
        ...garageOperations,
      },
    }));
  };

  checkNoteMarker(booking) {
    const { authUser } = this.props;
    const { notes, caseManagerId } = booking;

    if (isNotificationMarker(notes, authUser, caseManagerId)) {
      this.setState({ noteMarker: true });

      const note = {
        noteType: 'seen-note-marker',
        ownerType: 'booking',
        content: 'seen',
      };

      this.props.addNoteAction(note);
    }
  }

  render() {
    const {
      authUser,
      booking,
      getGarageBillPdfUrl,
      loading,
      cancelBooking,
      chargePayment,
      chargeInstalment,
      caseManagerId,
      bookingChanged,
      bookingReloading,
      reloadBooking,
      updateGarageBill,
    } = this.props;

    if (!booking || loading) {
      return <Loading />;
    }
    const {
      bookingId,
      garageId,
      masterStatusId,
      garageBills,
      quote,
      collectionDeliveryOption,
      garageStatus,
      customer,
      segment,
      cancelledQuoteId,
    } = booking;

    this.checkNoteMarker(booking);

    if (customer) {
      document.title = `${customer.firstName} ${customer.lastName}-${bookingId}`;
    }

    const contextValue = {
      booking,
      reloadBooking,
      setTags: this.setTags,
      addBookingNote: this.addBookingNote,
      updateTripAssignment: this.updateTripAssignment,
    };

    return (
      <StripeProvider apiKey={(config.stripe && config.stripe[segment]) || config.stripePublishableKey}>
        <BookingPageContext.Provider value={contextValue}>
          <Container className="perfect-table-fit">
            <LoadingModal
              visible={bookingReloading}
              title="Refreshing booking"
              subtitle="Please wait while the latest booking details load."
            />
            {bookingChanged && <RefreshBanner />}

            <BookingHeader
              addNoteAction={this.props.addNoteAction}
              authUser={authUser}
              booking={booking}
              cancelBooking={cancelBooking}
              chargePayment={chargePayment}
              chargeInstalment={chargeInstalment}
              caseManagerId={caseManagerId}
              patchBooking={this.props.patchBooking}
              segment={segment}
            />

            <MainDiv>
              {shouldShowKlarnaReauthorisationLink(booking) && (
                <Row>
                  <ColWithPadding>
                    <KlarnaReauthorisationAlert bookingId={bookingId} segment={segment} />
                  </ColWithPadding>
                </Row>
              )}
              <Row style={{ margin: 0 }} gutter={16}>
                <ColWithPadding span={12} className="js-booking-details-section">
                  <BookingDetails booking={booking} patchBooking={this.props.patchBooking} />
                  <CustomerNotes booking={booking} />
                </ColWithPadding>
                <ColWithPadding span={12}>
                  {['CUSTOMER_REVIEW'].includes(garageStatus) && (
                    <ReminderList reminders={booking.reminders} />
                  )}
                  <CaseManagerNotes
                    marker={this.state.noteMarker}
                    notes={booking.notes}
                    addNoteAction={this.props.addNoteAction}
                  />
                </ColWithPadding>
              </Row>

              {masterStatusId >= BOOKING_STATUS.ALLOCATED && collectionDeliveryOption !== 'CustomerC&D' && (
                <Row style={{ marginBottom: '-8px' }}>
                  <ColWithPadding>
                    <HeroTrips booking={booking} />
                  </ColWithPadding>
                </Row>
              )}

              <Row style={{ marginBottom: '-15px' }}>
                <ColWithPadding>
                  <GarageAllocation
                    booking={booking}
                    onGarageOperationsLoaded={this.handleGarageOperationsLoaded}
                  />
                </ColWithPadding>
              </Row>

              <Row className="row_work-operations">
                <ColWithPadding>
                  <WorkDetails garageOperations={this.state.garageOperations} />
                </ColWithPadding>
              </Row>

              {cancelledQuoteId && (
                <Row className="row_work-operations functional_cancelled-work-details">
                  <ColWithPadding>
                    <CancelledWorkDetails cancelledQuote={booking.cancelledQuote} />
                  </ColWithPadding>
                </Row>
              )}

              <Row className="media_gallery">
                <ColWithPadding>
                  <Gallery media={booking.media} />
                </ColWithPadding>
              </Row>

              {masterStatusId >= BOOKING_STATUS.WORK_COMPLETE && (
                <Row style={{ margin: 0 }} gutter={16}>
                  <ColWithPadding span={18}>
                    <GarageInvoiceDetails
                      garageBills={garageBills}
                      quote={quote}
                      updateGarageBill={updateGarageBill}
                      onDownloadBill={() => getGarageBillPdfUrl(garageId, bookingId)}
                    />
                  </ColWithPadding>
                </Row>
              )}
            </MainDiv>
          </Container>
        </BookingPageContext.Provider>
      </StripeProvider>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    chargePayment: (payment) => dispatch(chargePaymentAction(payment)),
    chargeInstalment: (payment) => dispatch(chargeInstalmentAction(payment)),
    addNoteAction: (note) => dispatch(addNoteAction(note)),
    cancelBooking: (patch, note, idempotencyKey) =>
      dispatch(cancelBookingAction(patch, note, idempotencyKey)),
    patchBooking: (patch, idempotencyKey) => dispatch(patchBookingAction(patch, idempotencyKey)),
    updateGarageBill: (garageBill) => dispatch(updateGarageBillStatus(garageBill)),
    getGarageBillPdfUrl: (garageId, bookingId) => dispatch(getGarageBillPdfUrlRequest(garageId, bookingId)),
    reloadBooking: () => dispatch(reloadBooking()),
    updateBooking: (booking) => dispatch(updateBookingAction(booking)),
  };
}

const mapStateToProps = createStructuredSelector({
  loading: getLoading(),
  booking: makeSelectBooking(),
  authUser: getAuthUser(),
  bookingChanged: getBookingChanged(),
  bookingReloading: getBookingReloading(),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withReducer = injectReducer({ key: 'booking-page', reducer });
const withSaga = injectSaga({ key: 'booking-page', saga });

export default withRouter(compose(withSaga, withReducer, withConnect)(BookingPage));
